import { useEffect, useMemo, useState } from "react";
import { IoCheckmarkCircle } from "react-icons/io5";
import { Checkbox, H3 } from "@datapay-by-datacom/design-system";
import {
  readApiWarnings,
  type SplitCostingImportError,
  type SplitCostingImportWorkflowResult,
} from "../hooks/useSplitCostingImportInvocation";
import {
  computeSplitCostingWarnings,
  mapSplitCostingApiWarnings,
} from "../utils";
import type { SplitCostingRow } from "../utils";
import { useFeatureFlagValue, FeatureFlag } from "../../feature-flags";
import styles from "./SplitCostingValidationReport.module.css";

export interface SplitCostingValidationReportProps {
  /** Narrowed `COMPLETED` result from `useSplitCostingImportInvocation`. */
  report: SplitCostingImportWorkflowResult;
  /**
   * Rows sent on invoke — joined against `errors[].index` to recover each
   * error's file row number and department path, neither of which the
   * workflow result carries directly (it only carries the 0-based array
   * position and, where applicable, the offending field name). Also the
   * source for flag-off client-side AC-APP-04/AC-APP-07 portal warnings, and
   * for mapping API warning `indices` to file row numbers when the flag is on.
   */
  rows: SplitCostingRow[];
  /**
   * Called whenever the acknowledgement state changes. Reports `true` once
   * every warning currently listed has been acknowledged — including the
   * trivial case of zero warnings. The Continue-to-commit gate (a later step
   * of `.plans/split-costing-import-validate-web.md`, V3) combines this with
   * `report.failed === 0`.
   */
  onWarningsAcknowledgedChange?: (allAcknowledged: boolean) => void;
}

interface DisplayError {
  fileRow: number | null;
  employeeCode: string | null;
  path: string;
  startDate: string;
  endDate: string;
  field: string | null;
  message: string;
}

function formatDateCell(value: string | null | undefined): string {
  return value ?? "—";
}

/**
 * Maps API field names (camelCase) to the column headers the user sees in
 * their CSV, so error table entries refer to the same label they uploaded.
 */
const FIELD_DISPLAY_NAMES: Record<string, string> = {
  apportionedPercentage: "Apportioned Percentage",
  endDate: "End date",
  startDate: "Start date",
  employeeCode: "Employee Code",
};

function displayFieldName(field: string | null): string | null {
  if (!field) return null;
  return FIELD_DISPLAY_NAMES[field] ?? field;
}

/**
 * Replaces any API field name tokens embedded in a message string with their
 * CSV column equivalents — e.g. "apportionedPercentage is required" becomes
 * "Apportioned Percentage is required".
 */
function displayMessage(message: string): string {
  let result = message;
  for (const [apiName, csvName] of Object.entries(FIELD_DISPLAY_NAMES)) {
    result = result.replaceAll(apiName, csvName);
  }
  return result;
}

function joinPath(row: SplitCostingRow | undefined): string {
  if (!row || row.path.length === 0) return "—";
  return row.path.map((level) => level.code).join("/");
}

function isRowError(
  error: SplitCostingImportError
): error is SplitCostingImportError & { index: number } {
  return error.index !== null;
}

/**
 * Presentational rendering of a `split_costing_import` validate result — see
 * `.plans/split-costing-import-validate-web.md` (V3) for the acceptance
 * criteria this maps to.
 *
 * - R-PRQ-1 (company has no cost structure) is a prerequisite error — it
 *   blocks the whole batch (`index === null`) and renders as a single banner,
 *   never as a row in the error table.
 * - The summary line intentionally omits a "succeeded" count: the workflow
 *   forces `succeeded=0` in validate mode so a dry run can never read as a
 *   partial write.
 * - When Datapay-aware validations are **on**, AC-APP-07 comes from
 *   `report.warnings` (Pacific R-VAL-3); AC-APP-04 is a blocking API error.
 *   Client `computeSplitCostingWarnings` is skipped so warnings are not
 *   double-rendered. When the flag is **off**, AC-APP-04/AC-APP-07 are still
 *   computed client-side from `rows`.
 * - Each warning requires individual acknowledgement before
 *   `onWarningsAcknowledgedChange` reports `true`.
 *
 * Out of scope here (follow-up steps of the same plan): Continue-to-commit
 * gating and Cancel, both of which live in `ValidateScreen`.
 */
export function SplitCostingValidationReport({
  report,
  rows,
  onWarningsAcknowledgedChange,
}: SplitCostingValidationReportProps) {
  const prerequisiteErrors = report.errors.filter((error) => error.index === null);

  const rowErrors: DisplayError[] = report.errors
    .filter(isRowError)
    .map((error) => {
      const row = rows[error.index];
      return {
        fileRow: row?.fileRow ?? null,
        employeeCode: error.employeeCode,
        path: joinPath(row),
        startDate: formatDateCell(row?.startDate),
        endDate: formatDateCell(row?.endDate),
        field: displayFieldName(error.field),
        message: displayMessage(error.error),
      };
    })
    .sort((a, b) => (a.fileRow ?? 0) - (b.fileRow ?? 0));

  const datapayAwareValidations = useFeatureFlagValue(
    FeatureFlag.EnableSplitCostingDatapayAwareValidations
  );
  const warnings = useMemo(
    () =>
      datapayAwareValidations
        ? mapSplitCostingApiWarnings(readApiWarnings(report.warnings), rows)
        : computeSplitCostingWarnings(rows),
    [datapayAwareValidations, report, rows]
  );

  // When blocking errors are present the warning checkboxes are locked (AC-19
  // pattern — matching work-pattern import). The callback must still report
  // `true` in that state so the Continue gate's `warningsAcknowledged` leg
  // doesn't get stuck; `report.failed === 0` is the independent gate that
  // keeps the button off.
  const blocked = report.failed > 0;

  // Acknowledgement resets whenever the warning set changes (e.g. a
  // re-upload produced a different `rows` reference) — a stale ack must not
  // silently carry over to a new batch.
  const [acknowledged, setAcknowledged] = useState<Set<number>>(new Set());
  useEffect(() => {
    setAcknowledged(new Set());
  }, [warnings]);

  const allWarningsAcknowledged =
    blocked ||
    warnings.length === 0 ||
    warnings.every((_, i) => acknowledged.has(i));

  useEffect(() => {
    onWarningsAcknowledgedChange?.(allWarningsAcknowledged);
    // `onWarningsAcknowledgedChange` intentionally excluded — callers that
    // don't memoize it would otherwise retrigger this effect every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allWarningsAcknowledged]);

  const toggleAcknowledged = (index: number) => {
    setAcknowledged((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <div className={styles.container}>
      {prerequisiteErrors.length > 0 && (
        <div className={styles.banner} role="alert">
          {prerequisiteErrors.map((error, i) => (
            <p key={i} className={styles.bannerMessage}>
              {error.error}
            </p>
          ))}
        </div>
      )}

      {report.failed === 0 && prerequisiteErrors.length === 0 ? (
        <div className={styles.successBanner} role="status">
          <IoCheckmarkCircle className={styles.successIcon} aria-hidden="true" />
          <div>
            <p className={styles.successTitle}>Validation passed</p>
            <p className={styles.successDetail}>
              {report.total} row{report.total === 1 ? "" : "s"} read, no blocking errors.
            </p>
          </div>
        </div>
      ) : (
        <p className={styles.summary}>
          {report.total} row{report.total === 1 ? "" : "s"} read, {report.failed}{" "}
          row{report.failed === 1 ? "" : "s"} with blocking errors.
        </p>
      )}

      {rowErrors.length > 0 && (
        <section aria-labelledby="validation-errors-heading">
          <H3 id="validation-errors-heading">Errors</H3>
          <div
            className={styles.tableWrapper}
            role="region"
            aria-label="Validation errors"
            tabIndex={0}
          >
            <table className={styles.errorTable}>
              <thead>
                <tr>
                  <th scope="col">Row</th>
                  <th scope="col">Employee code</th>
                  <th scope="col">Path</th>
                  <th scope="col">Start date</th>
                  <th scope="col">End date</th>
                  <th scope="col">Field</th>
                  <th scope="col">Message</th>
                </tr>
              </thead>
              <tbody>
                {rowErrors.map((error, i) => (
                  <tr key={i}>
                    <td className={styles.cell}>{error.fileRow ?? "—"}</td>
                    <td className={styles.cell}>{error.employeeCode ?? "—"}</td>
                    <td className={styles.cell}>{error.path}</td>
                    <td className={styles.cell}>{error.startDate}</td>
                    <td className={styles.cell}>{error.endDate}</td>
                    <td className={styles.cell}>{error.field ?? "—"}</td>
                    <td className={styles.cell}>{error.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {warnings.length > 0 && (
        <section
          aria-labelledby="validation-warnings-heading"
          className={styles.warningSection}
        >
          <H3 id="validation-warnings-heading">Warnings</H3>
          <p className={styles.warningIntro}>
            {blocked
              ? "Fix the blocking errors above first. You'll also need to acknowledge the below before you can commit."
              : "These do not block the import, but each must be acknowledged before you can continue to commit."}
          </p>
          <ul className={styles.warningList}>
            {warnings.map((warning, i) => (
              <li key={i} className={styles.warningItem}>
                {blocked ? (
                  warning.message
                ) : (
                  <Checkbox
                    isSelected={acknowledged.has(i)}
                    onChange={() => toggleAcknowledged(i)}
                  >
                    {warning.message}
                  </Checkbox>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
